import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

const acknowledgementKey = "qotta.privacyNotice.v1";
// Informational UI state only; retained across route remounts if storage is blocked.
let acknowledgedThisRuntime = false;

export function PrivacyNotice() {
  const [phase, setPhase] = useState(() => {
    if (acknowledgedThisRuntime) return "hidden";
    try {
      if (window.localStorage.getItem(acknowledgementKey) === "1") return "hidden";
    } catch { /* Storage is optional for this notice. */ }
    return "visible";
  });
  const dismissing = useRef(false);

  useEffect(() => {
    if (phase !== "closing") return;
    const timeout = window.setTimeout(() => setPhase("hidden"), 350);
    return () => window.clearTimeout(timeout);
  }, [phase]);

  if (phase === "hidden") return null;

  const acknowledge = () => {
    if (dismissing.current) return;
    dismissing.current = true;
    acknowledgedThisRuntime = true;
    try {
      window.localStorage.setItem(acknowledgementKey, "1");
    } catch { /* The runtime acknowledgement still dismisses the notice. */ }
    setPhase(window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "hidden" : "closing");
  };

  return (
    <section
      aria-labelledby="privacy-notice-title"
      lang="en"
      dir="ltr"
      data-state={phase}
      inert={phase === "closing" ? true : undefined}
      className="privacy-notice surface-elevated fixed bottom-4 left-4 z-40 w-[calc(100%-2rem)] max-w-[420px] p-5 text-foreground sm:bottom-6 sm:right-6 sm:left-auto print:hidden"
      onAnimationEnd={() => { if (phase === "closing") setPhase("hidden"); }}
    >
      <h2 id="privacy-notice-title" className="text-sm font-bold">Your privacy matters</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Qotta uses essential browser technologies, such as cookies and local storage, to provide its services and remember your progress.
      </p>
      <div className="mt-4 flex items-center justify-between gap-4">
        <Button asChild variant="link" className="h-11 px-0 motion-reduce:transform-none">
          <Link to="/privacy">Privacy Policy</Link>
        </Button>
        <Button type="button" onClick={acknowledge} className="h-11 px-6 motion-reduce:transform-none">Got it</Button>
      </div>
    </section>
  );
}
